import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight, X, BookOpen } from 'lucide-react';

/**
 * NewConceptBanner — Bannière éditoriale "NOUVEAU · Une façon différente de réviser"
 *
 * Refonte mai 2026 : annonce le pivot pédagogique du site (magazine + QCM contextuel
 * en remplacement du concept "examen blanc"). Placée en tête de la Home, sous la Topbar.
 *
 * - Refermable (persistance localStorage)
 * - Animation subtle d'entrée
 * - Lien direct vers /enquetes (vitrine du nouveau format)
 */
const STORAGE_KEY = 'c2r-new-concept-banner-dismissed-v1';

export default function NewConceptBanner() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(STORAGE_KEY);
      if (!dismissed) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  const handleDismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* localStorage indisponible (mode privé) : on accepte la perte de l'état */
    }
  };

  return (
    <section
      data-testid="new-concept-banner"
      className="relative overflow-hidden rounded-2xl border-2 border-[hsl(var(--primary))]/30 bg-gradient-to-r from-[#cf2031]/8 via-[#cf2031]/4 to-transparent dark:from-[#cf2031]/14 dark:via-[#cf2031]/8 dark:to-transparent"
      aria-label="Nouveauté : une façon différente de réviser"
    >
      {/* Décor magazine subtle */}
      <div
        className="absolute -top-12 -right-12 w-44 h-44 rounded-full blur-3xl opacity-25 pointer-events-none"
        style={{ background: '#cf2031' }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(45deg, transparent 0 14px, hsl(var(--foreground)) 14px 15px)',
        }}
        aria-hidden="true"
      />

      <div className="relative px-3 sm:px-6 py-3 sm:py-5 flex items-center gap-2.5 sm:gap-5">
        {/* Pastille NOUVEAU desktop avec icône livre */}
        <div className="hidden sm:flex flex-col items-center flex-shrink-0">
          <span
            className="inline-flex items-center gap-1 text-[9px] sm:text-[10px] font-black uppercase tracking-[0.22em] px-2.5 py-1 rounded-full text-white shadow-sm"
            style={{ background: '#cf2031' }}
          >
            <Sparkles className="w-2.5 h-2.5" />
            Nouveau
          </span>
          <BookOpen className="w-7 h-7 text-[hsl(var(--primary))] mt-2" />
        </div>

        {/* Mobile : icône livre seule (le badge NOUVEAU passe en inline dans le titre) */}
        <div className="flex sm:hidden flex-shrink-0">
          <BookOpen className="w-5 h-5 text-[hsl(var(--primary))]" aria-hidden="true" />
        </div>

        {/* Texte principal */}
        <div className="flex-1 min-w-0">
          <div className="flex sm:hidden items-center gap-1.5 mb-1">
            <span
              className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-[0.18em] px-2 py-0.5 rounded-full text-white"
              style={{ background: '#cf2031' }}
            >
              <Sparkles className="w-2.5 h-2.5" />
              Nouveau
            </span>
          </div>
          <h2
            className="text-sm sm:text-xl lg:text-2xl font-black tracking-tight leading-tight text-foreground"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Une façon différente de réviser.
          </h2>
          {/* Description : masquée en mobile pour ne plus écraser le hero qui suit */}
          <p className="hidden sm:block text-xs sm:text-sm text-foreground/75 leading-relaxed mt-1 max-w-2xl">
            Découvre nos <strong>31 enquêtes magazine</strong> + <strong>9 dossiers pédagogiques</strong> :
            témoignages, statistiques, cas réels — sans chrono, sans score. Tu apprends en lisant,
            à ton rythme.
          </p>
          {/* Description mobile ultra-courte */}
          <p className="sm:hidden text-[11px] text-foreground/70 leading-snug mt-0.5">
            <strong>31 enquêtes</strong> + 9 dossiers magazine
          </p>
        </div>

        {/* Actions — alignées verticalement au centre du bloc texte */}
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 self-center">
          <button
            type="button"
            onClick={() => navigate('/enquetes')}
            className="inline-flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-4 h-8 sm:h-10 rounded-full bg-[hsl(var(--primary))] text-white text-[11px] sm:text-sm font-bold hover:bg-[hsl(var(--primary))]/90 transition-all hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[hsl(var(--primary))]"
            data-testid="new-concept-banner-cta"
            aria-label="Découvrir les 31 enquêtes magazine"
          >
            <span className="hidden sm:inline">Découvrir</span>
            <span className="sm:hidden">Voir</span>
            <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="w-7 h-7 sm:w-9 sm:h-9 rounded-full grid place-items-center text-foreground/55 hover:text-foreground hover:bg-foreground/8 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]"
            data-testid="new-concept-banner-dismiss"
            aria-label="Fermer cette annonce"
          >
            <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
